/**
 * Weight conversion and display helpers.
 * API values are already in the user's selected unit; backend handles conversion.
 */
export const LBS_PER_KG = 2.20462;

export type WeightUnitCode = 'kg' | 'lbs';

/** Unit tokens the weight screens store, mirroring `HEIGHT_UNIT`. */
export const WEIGHT_UNIT = {
  KG: 'kg',
  LBS: 'lbs',
} as const satisfies Record<string, WeightUnitCode>;

/** OnboardingProfileInfo WeightUnit enum (never send UI labels like "lbs" / "kg"). */
export type OnboardingWeightUnit = 'WeightKG' | 'WeightLBS';

export const ONBOARDING_WEIGHT_UNIT = {
  KG: 'WeightKG',
  LBS: 'WeightLBS',
} as const satisfies Record<string, OnboardingWeightUnit>;

/** Backend ScaleUnit lookup — Weight rows. */
export const WEIGHT_SCALE_UNIT_ID = {
  KG: 1,
  LBS: 2,
} as const;

export const normalizeWeightUnit = (unit?: string | null): WeightUnitCode => {
  const u = String(unit ?? '')
    .toLowerCase()
    .trim();
  if (
    u === 'lbs' ||
    u === 'lb' ||
    u === 'weightlbs' ||
    u === '2' ||
    u.includes('lb')
  ) {
    return 'lbs';
  }
  return 'kg';
};

/** Map UI weight preference → OnboardingProfileInfo WeightUnit enum. */
export const toOnboardingWeightUnit = (
  unit?: string | null,
): OnboardingWeightUnit =>
  normalizeWeightUnit(unit) === 'lbs'
    ? ONBOARDING_WEIGHT_UNIT.LBS
    : ONBOARDING_WEIGHT_UNIT.KG;

/** Map UI / preference unit → ScaleUnitID (1 = KG, 2 = LBS). */
export const toWeightScaleUnitId = (unit?: string | null): number =>
  normalizeWeightUnit(unit) === 'lbs'
    ? WEIGHT_SCALE_UNIT_ID.LBS
    : WEIGHT_SCALE_UNIT_ID.KG;

/** Convert lbs → kg (3 decimal places, matches API examples e.g. 65.770). */
export const lbsToKgPrecise = (lbs: number): number =>
  Number((lbs / LBS_PER_KG).toFixed(3));

/** Convert kg → lbs for display (nearest whole pound). */
export const kgToLbsPrecise = (kg: number): number =>
  Math.round(kg * LBS_PER_KG);

/**
 * Resolve a weight value to kilograms (legacy helper for callers that still need it).
 */
export const toWeightKg = (value: number, unit?: string | null): number => {
  if (!Number.isFinite(value)) return 0;
  return normalizeWeightUnit(unit) === 'lbs'
    ? lbsToKgPrecise(value)
    : Number(Number(value).toFixed(3));
};

/**
 * Convert a kilogram value into the user's preferred display unit.
 */
export const fromWeightKg = (
  kg: number,
  displayUnit?: string | null,
): number => {
  if (!Number.isFinite(kg)) return 0;
  return normalizeWeightUnit(displayUnit) === 'lbs'
    ? kgToLbsPrecise(kg)
    : Number(Number(kg).toFixed(1));
};

/**
 * Format a value that is already in the preferred display unit.
 */
export const formatWeightInUnit = (
  value: number | null | undefined,
  unit?: string | null,
): string => {
  if (value == null || !Number.isFinite(Number(value))) return '';
  const u = normalizeWeightUnit(unit);
  if (u === 'lbs') {
    return `${Math.round(Number(value))} lbs`;
  }
  return `${Number(Number(value).toFixed(1))} kg`;
};

/**
 * Format weight for UI. Value is already in the preferred unit (backend converts).
 * Example: (145, 'lbs') → "145 lbs"
 */
export const formatWeightDisplay = (
  weight: number | null | undefined,
  unit?: string | null,
): string => formatWeightInUnit(weight, unit);

/** Integer helpers used by buddy-search sliders (legacy). */
export const kgToLbs = (kg: number): number => Math.round(kg * LBS_PER_KG);

export const lbsToKg = (lbs: number): number => Math.round(lbs / LBS_PER_KG);

/**
 * Calculates the weight progress percentage based on start, current, and goal weight.
 */
export const calculateWeightProgress = (
  startWeight?: number | null,
  currentWeight?: number | null,
  goalWeight?: number | null,
): number => {
  if (startWeight == null || currentWeight == null || goalWeight == null) {
    return 0;
  }

  const totalToLose = startWeight - goalWeight;
  const lostWeight = startWeight - currentWeight;

  if (totalToLose === 0) {
    return currentWeight <= goalWeight ? 100 : 0;
  }

  return Math.max(0, Math.min(100, (lostWeight / totalToLose) * 100));
};
